import Axios from "axios";
import { Formik } from "formik";
import * as React from "react";
import { useEffect, useState } from "react";
import { MultiSelect } from "react-multi-select-component";
import { RouteComponentProps } from "react-router-dom";
import { CreateNoteValildationErrors } from "../Models/CreateNoteValidationErrors";
import { CustomError } from "../Models/CustomError";
import { Note as NoteModel } from "../Models/Note";
import { EditNote as EditNoteModel } from "../Models/EditNote";
import { Options } from "../Models/Options";
import { Tag } from "../Models/Tag";
import { isCustomError } from "../Utils";

interface IProps extends RouteComponentProps {
    noteToEditID: string;
}

export const EditNote: React.FunctionComponent<IProps> = (
    props: IProps
): JSX.Element => {
    const [postErrors, setPostErrors] = useState<CustomError>();
    const { history, noteToEditID } = props;
    const [noteToEdit, setNoteToEdit] = useState<NoteModel>({
        post: "",
        title: "",
        tags: [],
    });

    const [tagArr, setTagArr] = useState<Tag[]>([]);
    const [selectedTags, setSelectedTags] = useState<Options[]>([]);

    const onCancel = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        event?.preventDefault();
        history.push("/");
    };

    useEffect(() => {
        async function getNote() {
            try {
                const response = await Axios.get(
                    `${process.env.REACT_APP_API_URL}/api/v1/notes/${noteToEditID}`
                );
                const note = response.data as NoteModel;
                setNoteToEdit(note);
                setSelectedTags(
                    note.tags.map((tag) => {
                        return { label: tag.name, value: tag.id };
                    })
                );
            } catch (error) {
                if(isCustomError(error)){
                    setPostErrors(error);
                }
            }
        }
        getNote();

        async function getTags() {
            try {
                const response = await Axios.get(
                    `${process.env.REACT_APP_API_URL}/api/v1/tags`
                );
                const tags = response.data as Tag[];
                setTagArr(tags);
            } catch (error) {
                if(isCustomError(error)){
                    setPostErrors(error);
                }
            }
        }
        getTags();
    }, [noteToEditID]);

    return (
        <Formik
            enableReinitialize
            initialValues={noteToEdit}
            validate={(values: NoteModel) => {
                const errors: CreateNoteValildationErrors = {
                    post: "",
                    title: "",
                };
                if (!values.title) {
                    errors.title = "Required";
                }
                if (!values.post) {
                    errors.post = "Required";
                }
                if (
                    errors.title !== "" ||
                    errors.post !== ""
                ) {
                    return errors;
                }
                return {};
            }}
            onSubmit={async (values: NoteModel, { setSubmitting }) => {
                //save new note
                let editNote: EditNoteModel = { 
                    post: values.post,
                    title: values.title,
                    tagIds: selectedTags?.map(tag => tag.value),
                };
                try {
                    await Axios.put(
                        `${process.env.REACT_APP_API_URL}/api/v1/notes/${noteToEditID}`,
                        editNote
                    );
                    history.push("/");
                } catch (error) {
                    if(isCustomError(error)){
                        setPostErrors(error);
                    }
                }

                setSubmitting(false);
            }}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
            }) => (
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Title:</label>
                        <input
                            type="text"
                            className="form-control"
                            id="title"
                            name="title"
                            onChange={handleChange("title")}
                            value={values.title}
                            onBlur={handleBlur}
                        />
                        {touched.title && (
                            <span className="text-muted">{errors.title}</span>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Tags:</label>

                        <MultiSelect
                            options={tagArr.map((tag: Tag) => {
                                return { label: tag.name, value: tag.id };
                            })}
                            value={selectedTags}
                            labelledBy={"Select"}
                            onChange={setSelectedTags}
                        />
                    </div>

                    <div className="form-group">
                        <label>Post:</label>
                        <textarea
                            className="form-control"
                            id="post"
                            name="post"
                            onChange={handleChange("post")}
                            value={values.post}
                            onBlur={handleBlur}
                        />
                        {touched.post && (
                            <span className="text-muted">{errors.post}</span>
                        )}
                    </div>

                    <button
                        className="btn btn-primary"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        Save
                    </button>
                    <button className="btn btn-primary" onClick={onCancel}>
                        Cancel
                    </button>

                    {postErrors?.errors.map((error: string) => (
                        <small className="form-text text-muted">{error}</small>
                    ))}
                </form>
            )}
        </Formik>
    );
};
